import {
  type SellerProductOffersSendAllJobsRequest,
  type SellerOfferResponseType,
  type SellerProductOffersQuery,
  type SubmitSellerOfferRequest,
  type UpdateAutoSendOfferSettingsRequest,
} from './aliases';
import { type LikesNotificationsResponse } from './types';

import {
  SELLER_AUTO_SEND_OFFER_SETTINGS,
  SELLER_CAN_SET_AUTO_SEND_OFFER_SETTINGS,
  SELLER_LIKES_NOTIFICATIONS,
  SELLER_OFFER_RESPONSE,
  SELLER_OFFERS_FOR_PRODUCT,
  SELLER_PRODUCTS_WITH_OFFERS,
  SELLER_SEND_OFFER_ALL,
  SELLER_SEND_OFFER_ALL_STATUS,
  SELLER_START_SEND_OFFER_ALL,
} from '@/constants/endpoints';
import http, { typedHttp } from '@/modules/http';
import { replacePathParams } from '@/modules/http/replacePathParams';
import { type AxiosCompatibleResponse } from '@/modules/http/types';

type GetProductOffersParams = {
  productId: string;
  active: boolean;
  variantId?: string;
  after?: string;
};

export function getProductOffers({
  productId,
  active,
  variantId,
  after,
}: GetProductOffersParams) {
  return typedHttp.get(
    replacePathParams(SELLER_OFFERS_FOR_PRODUCT, { productId }),
    {
      params: {
        active: active ? 'true' : 'false',
        include_size: 'true',
        ...(variantId && { variant_id: variantId }),
        ...(after && { after }),
      } satisfies SellerProductOffersQuery,
      withAuth: true,
      withDeviceId: true,
    }
  );
}

export function getSellerProductWithOffers(after?: string) {
  return typedHttp.get(SELLER_PRODUCTS_WITH_OFFERS, {
    params: {
      ...(after && { after }),
    },
    withAuth: true,
    withDeviceId: true,
  });
}

export async function fetchLikesNotifications(
  cursor?: string
): Promise<AxiosCompatibleResponse<LikesNotificationsResponse>> {
  return await http.get(SELLER_LIKES_NOTIFICATIONS, {
    params: { ...(cursor && { cursor }) },
    withAuth: true,
    withDeviceId: true,
  });
}

export function createSellerOffer(
  productId: string,
  sellerSendOfferData: SubmitSellerOfferRequest
) {
  return typedHttp.post(
    replacePathParams(SELLER_OFFERS_FOR_PRODUCT, { productId }),
    sellerSendOfferData,
    { withAuth: true, withDeviceId: true }
  );
}

export async function respondToOffer({
  responseType,
  productId,
  offerId,
}: {
  responseType: SellerOfferResponseType;
  productId: string;
  offerId: string;
}) {
  const resp = await typedHttp.post(
    replacePathParams(SELLER_OFFER_RESPONSE, { productId, offerId }),
    { seller_response: responseType },
    { withAuth: true, withDeviceId: true }
  );
  return resp.data;
}

export async function startSellerOfferAll(productId: number) {
  const resp = await typedHttp.get(
    replacePathParams(SELLER_START_SEND_OFFER_ALL, {
      productId: productId.toString(),
    }),
    { withAuth: true, withDeviceId: true }
  );

  return resp?.data;
}

export async function sendSellerOfferAll(
  productId: number,
  sellerSendOffersAllData: SellerProductOffersSendAllJobsRequest
) {
  return await typedHttp.post(
    replacePathParams(SELLER_SEND_OFFER_ALL, {
      productId: productId.toString(),
    }),
    sellerSendOffersAllData,
    { withAuth: true, withDeviceId: true }
  );
}

export function getAutoSendOffer(productId: number) {
  return typedHttp.get(
    replacePathParams(SELLER_AUTO_SEND_OFFER_SETTINGS, {
      productId: productId.toString(),
    }),
    { withAuth: true, withDeviceId: true }
  );
}

export function updateAutoSendOffer(
  productId: number,
  body: UpdateAutoSendOfferSettingsRequest
) {
  return typedHttp.post(
    replacePathParams(SELLER_AUTO_SEND_OFFER_SETTINGS, {
      productId: productId.toString(),
    }),
    body,
    { withAuth: true, withDeviceId: true }
  );
}

export function deleteAutoSendOffer(productId: number) {
  return typedHttp.delete(
    replacePathParams(SELLER_AUTO_SEND_OFFER_SETTINGS, {
      productId: productId.toString(),
    }),
    { withAuth: true, withDeviceId: true }
  );
}

export function getCanSetAutoSendOffer(productId: number) {
  return typedHttp.get(
    replacePathParams(SELLER_CAN_SET_AUTO_SEND_OFFER_SETTINGS, {
      productId: productId.toString(),
    }),
    { withAuth: true, withDeviceId: true }
  );
}

export async function getSellerSendOfferAllStatus(
  productId: number,
  jobId: string
) {
  return await typedHttp.get(
    replacePathParams(SELLER_SEND_OFFER_ALL_STATUS, {
      productId: productId.toString(),
      jobId,
    }),
    { withAuth: true, withDeviceId: true }
  );
}
